import { useNavigate, useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import { IAlarm } from '_/api/alarms/alarms.api';
import AsyncPageContainer from '_/common/components/layout/AsyncPageContainer';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { ALARM_COMPARISON_OPERATORS, ALARM_METRIC_OPTIONS, DEFAULT_ALARM_README, ALARM_SEVERITY } from '_/constants/common';
import { useAlarmGroupContext } from '_/contexts/AlarmGroupContext';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useAlarm, useCreateAlarm, useUpdateAlarm, useUpdateAlarmReadme } from '_/hooks/useAlarms';
import { checkPositiveNumber } from '_/utils/formValidations';
import { Form, Input, Select, Checkbox, Button } from 'antd';
import React, { useEffect, useState, ReactElement, useRef } from 'react';

interface NewAlarmProps {
  match?: {
    params?: {
      alarmId?: string;
    };
  };
}

interface AlarmFormValues {
  name: string;
  metric: string;
  threshold: number | undefined;
  comparisonOperator: string;
  active: boolean;
  severity: string;
}

function NewAlarm({ match }: NewAlarmProps): ReactElement {
  const [form] = Form.useForm<AlarmFormValues>();
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
    flowSlug: string;
    alarmGroupId: string;
  }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const flowSlug = params.flowSlug || '';
  const alarmGroupId = params.alarmGroupId || '';

  const alarmId = match?.params?.alarmId;
  const isNewAlarm = !alarmId || alarmId === '_new';

  const [isEditMode] = useState(!isNewAlarm);
  const [alarmReadme, setAlarmReadme] = useState('');
  const hasInitialized = useRef(false);

  // Context hooks
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { selectedFlow } = useFlowContext();
  const { selectedAlarmGroup } = useAlarmGroupContext();
  const { setBreadcrumbs } = useTopHeader();

  // Fetch alarm when editing (skip when creating new)
  const { data: fetchedAlarm, isLoading: isFetchingAlarm } = useAlarm(isNewAlarm ? undefined : alarmId);
  const selectedAlarm = (fetchedAlarm as IAlarm) || null;
  const selectedAlarmReadme = selectedAlarm?.readme?.content;

  // React Query mutations
  const createAlarmMutation = useCreateAlarm(alarmGroupId);
  const updateAlarmMutation = useUpdateAlarm(selectedAlarm?.id, alarmGroupId);
  const updateAlarmReadmeMutation = useUpdateAlarmReadme(selectedAlarm?.id, alarmGroupId);

  // Initialize form when creating a new alarm
  useEffect(() => {
    if (isNewAlarm && !hasInitialized.current) {
      setAlarmReadme(`## ALARM README

${DEFAULT_ALARM_README}`);

      // Set default values for new alarm
      form.setFieldsValue({
        name: '',
        metric: '',
        threshold: undefined,
        comparisonOperator: '',
        active: true,
        severity: ALARM_SEVERITY.CRITICAL,
      });
      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewAlarm]);

  // Populate form when alarm is loaded
  useEffect(() => {
    if (isEditMode && selectedAlarm && selectedAlarm.id) {
      form.setFieldsValue({
        name: selectedAlarm.name,
        metric: selectedAlarm.metric,
        threshold: selectedAlarm.threshold,
        comparisonOperator: selectedAlarm.comparisonOperator,
        active: selectedAlarm.active,
        severity: selectedAlarm.severity,
      });
    }
  }, [isEditMode, selectedAlarm, form]);

  // Set readme when alarm is loaded
  useEffect(() => {
    if (selectedAlarmReadme) {
      setAlarmReadme(selectedAlarmReadme);
    }
  }, [selectedAlarmReadme]);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}`,
      },
      {
        title: 'Alarm Groups',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups`,
      },
      {
        title: selectedAlarmGroup?.name || 'Alarm Group',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${alarmGroupId}`,
      },
      {
        title: 'Alarms',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${alarmGroupId}/alarms`,
      },
      { title: isNewAlarm ? 'New Alarm' : selectedAlarm?.name || 'Edit Alarm' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization?.name,
    orgSlug,
    selectedProject?.name,
    projectSlug,
    selectedFlow?.name,
    flowSlug,
    selectedAlarmGroup?.name,
    alarmGroupId,
    isNewAlarm,
    selectedAlarm?.name,
  ]);

  const onBackClick = () => {
    void history({
      to: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms`,
    });
  };

  const onFinish = async (values: AlarmFormValues) => {
    if (isEditMode && selectedAlarm) {
      // Update alarm fields if changed
      const updatedAlarm = {
        ...(selectedAlarm.active !== values.active && { active: values.active }),
        ...(selectedAlarm.name !== values.name && { name: values.name }),
        ...(selectedAlarm.metric !== values.metric && { metric: values.metric }),
        ...(selectedAlarm.threshold !== values.threshold && {
          threshold: Number(values.threshold),
        }),
        ...(selectedAlarm.comparisonOperator !== values.comparisonOperator && {
          comparisonOperator: values.comparisonOperator,
        }),
        ...(selectedAlarm.severity !== values.severity && { severity: values.severity }),
      };

      // Update alarm if any fields changed
      if (Object.keys(updatedAlarm).length > 0) {
        await updateAlarmMutation.mutateAsync(updatedAlarm);
      }

      // Update readme if changed
      if (alarmReadme !== selectedAlarmReadme) {
        await updateAlarmReadmeMutation.mutateAsync(alarmReadme);
      }

      // Navigate back to alarms list
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms`,
        replace: true,
      });
    } else {
      // Create new alarm
      const newAlarm = {
        name: values.name,
        groupId: alarmGroupId!,
        active: values.active,
        severity: values.severity,
        metric: values.metric,
        threshold: Number(values.threshold),
        comparisonOperator: values.comparisonOperator,
      };

      await createAlarmMutation.mutateAsync({ alarm: newAlarm, readme: alarmReadme });

      // Navigate back to alarms list
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms`,
      });
    }
  };

  const handleReadmeChange = (value: string) => {
    setAlarmReadme(value);
  };

  // Ant Design Form will handle validation automatically when submit is triggered

  // Only show loader when editing an existing alarm and it's loading
  const shouldShowLoader = isEditMode && isFetchingAlarm;

  return (
    <AsyncPageContainer isLoading={shouldShowLoader}>
      <PageContent>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            active: true,
            severity: ALARM_SEVERITY.CRITICAL,
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter alarm name' }]}
              style={{ flex: 1, marginBottom: 16 }}
            >
              <Input placeholder="Alarm name" />
            </Form.Item>

            <Form.Item name="active" valuePropName="checked" style={{ marginTop: 30 }}>
              <Checkbox>Active</Checkbox>
            </Form.Item>
          </div>
          <Form.Item name="severity" label="Severity" rules={[{ required: true, message: 'Please select severity' }]}>
            <Select placeholder="Select severity">
              {Object.keys(ALARM_SEVERITY).map((severity) => (
                <Select.Option key={ALARM_SEVERITY[severity]} value={ALARM_SEVERITY[severity]}>
                  {ALARM_SEVERITY[severity]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="metric" label="Metric" rules={[{ required: true, message: 'Please select metric' }]}>
            <Select placeholder="Select metric" showSearch optionFilterProp="children">
              {Object.keys(ALARM_METRIC_OPTIONS).map((subheader) => {
                const options: JSX.Element[] = [];

                if (typeof ALARM_METRIC_OPTIONS[subheader] === 'string') {
                  options.push(
                    <Select.OptGroup key={subheader} label={subheader}>
                      <Select.Option value={subheader}>{subheader}</Select.Option>
                    </Select.OptGroup>,
                  );
                } else {
                  const groupOptions: JSX.Element[] = [];
                  Object.keys(ALARM_METRIC_OPTIONS[subheader]).forEach((metricSection) => {
                    if (Array.isArray(ALARM_METRIC_OPTIONS[subheader][metricSection])) {
                      ALARM_METRIC_OPTIONS[subheader][metricSection].forEach((metric: string) => {
                        groupOptions.push(
                          <Select.Option key={`${metric}-${metricSection}`} value={`${subheader}.${metricSection}.${metric}`}>
                            {metricSection} - {metric}
                          </Select.Option>,
                        );
                      });
                    } else {
                      groupOptions.push(
                        <Select.Option key={`${metricSection}-${subheader}`} value={`${subheader}.${metricSection}`}>
                          {metricSection}
                        </Select.Option>,
                      );
                    }
                  });
                  options.push(
                    <Select.OptGroup key={subheader} label={subheader}>
                      {groupOptions}
                    </Select.OptGroup>,
                  );
                }

                return options;
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="comparisonOperator"
            label="Comparison Operator"
            rules={[{ required: true, message: 'Please select comparison operator' }]}
          >
            <Select placeholder="Select comparison operator">
              {ALARM_COMPARISON_OPERATORS.map((option) => (
                <Select.Option value={option} key={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="threshold"
            label="Threshold"
            rules={[
              { required: true, message: 'Please enter threshold' },
              {
                validator: (_, value) => {
                  if (value !== undefined && !checkPositiveNumber(value)) {
                    return Promise.reject('Please enter a positive number');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="number" step="0.01" placeholder="Enter threshold value" />
          </Form.Item>

          <div style={{ marginBottom: 55, marginTop: 25 }}>
            <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={handleReadmeChange} value={alarmReadme} />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button onClick={onBackClick}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </Form>
      </PageContent>
    </AsyncPageContainer>
  );
}

export default NewAlarm;
